import React, { useEffect, useRef, useState, useCallback } from "react";
import { Mic, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";

/**
 * Fire-and-forget audio → task capture.
 *
 * Tap once → start recording (auto-stops after 12s of silence or 30s hard cap).
 * Tap again → stop early.
 * Audio → /api/transcribe (Whisper) → /api/ai/mission with silent=true.
 * The backend parses the utterance into task actions without emitting a spoken reply,
 * so the round-trip is much faster than a full interactive Comms turn.
 *
 * Props:
 *   - onCaptured(result)  called with the mission response after each capture completes
 *   - variant             "fab" (large floating), "dock" (medium), "icon" (compact nav icon)
 *   - className           extra classes for positioning
 *   - testid              data-testid override
 */
export const QuickCaptureButton = ({ onCaptured, variant = "fab", className = "", testid = "quick-capture-btn" }) => {
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const stopTimerRef = useRef(null);
    const silenceTimerRef = useRef(null);

    // Clean up any live tracks/timers if the component unmounts mid-record
    useEffect(() => {
        return () => {
            recorderRef.current?.stop?.();
            streamRef.current?.getTracks?.().forEach((t) => t.stop());
            clearTimeout(stopTimerRef.current);
            clearTimeout(silenceTimerRef.current);
        };
    }, []);

    const stopEarly = useCallback(() => {
        try { recorderRef.current?.stop?.(); } catch { /* noop */ }
    }, []);

    const start = useCallback(async (e) => {
        e?.stopPropagation?.();
        if (processing) return;
        if (recording) {
            stopEarly();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const rec = new MediaRecorder(stream);
            recorderRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
            rec.onstop = async () => {
                setRecording(false);
                clearTimeout(stopTimerRef.current);
                clearTimeout(silenceTimerRef.current);
                streamRef.current?.getTracks?.().forEach((t) => t.stop());
                if (chunksRef.current.length === 0) {
                    toast.warning("Nothing recorded");
                    return;
                }
                setProcessing(true);
                const capturedToast = toast.loading("Captured — processing…");
                try {
                    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
                    const { text } = await api.transcribe(blob);
                    const clean = (text || "").trim();
                    if (!clean) {
                        toast.dismiss(capturedToast);
                        toast.warning("Nothing heard");
                        return;
                    }
                    // Silent-mode chat: backend parses actions, skips spoken reply + TTS.
                    const res = await api.aiMission(clean, [], "quick_capture", true);
                    toast.dismiss(capturedToast);
                    const createdCount = (res.created || []).length;
                    if (createdCount > 0) {
                        toast.success(`Added ${createdCount} task${createdCount === 1 ? "" : "s"}`, {
                            description: clean.length > 60 ? clean.slice(0, 57) + "…" : clean,
                        });
                    } else {
                        toast.info("Captured", { description: clean.length > 60 ? clean.slice(0, 57) + "…" : clean });
                    }
                    onCaptured?.(res);
                } catch (err) {
                    toast.dismiss(capturedToast);
                    toast.error("Capture failed — try again");
                    console.error(err);
                } finally {
                    setProcessing(false);
                }
            };
            // Timeslice = flush a chunk every 500ms so ondataavailable fires reliably before onstop,
            // even on mobile browsers that otherwise batch a single blob only at stop-time.
            rec.start(500);
            setRecording(true);
            // Hard cap 60s so a stuck record doesn't drain the mic indefinitely.
            stopTimerRef.current = setTimeout(stopEarly, 60_000);
        } catch (err) {
            toast.error("Microphone unavailable");
            console.error(err);
        }
    }, [recording, processing, stopEarly, onCaptured]);

    const sizeCls =
        variant === "fab"    ? "w-14 h-14"
      : variant === "dock"   ? "w-10 h-10"
      : variant === "inline" ? "px-4 py-2 gap-2 h-11"       // pill with label
      :                        "w-9 h-9";
    const iconSize = variant === "fab" ? 22 : variant === "dock" || variant === "inline" ? 18 : 16;

    // Inline variant renders a wide pill with explicit "Tap to speak / STOP recording" text so the
    // start/stop state is unambiguous. Requested by user — the icon-only FAB was hard to read.
    if (variant === "inline") {
        return (
            <button
                type="button"
                onClick={start}
                disabled={processing}
                className={`inline-flex items-center rounded-full transition-all ${sizeCls} font-brand font-bold uppercase tracking-widest text-xs ${
                    recording
                        ? "bg-red-500 text-white animate-pulse shadow-[0_0_28px_rgba(239,68,68,0.6)]"
                        : processing
                            ? "bg-meye-fuchsia/25 text-meye-fuchsia"
                            : "bg-gradient-to-br from-meye-fuchsia to-fuchsia-700 text-white shadow-[0_0_20px_rgba(217,70,239,0.5)] hover:scale-105"
                } ${className}`}
                data-testid={testid}
                aria-label={recording ? "Stop recording" : processing ? "Processing capture" : "Tap to speak"}
            >
                {processing ? (
                    <>
                        <Loader2 size={iconSize} className="animate-spin" />
                        <span>Processing…</span>
                    </>
                ) : recording ? (
                    <>
                        <span className="w-3 h-3 rounded-sm bg-white" />
                        <span>Stop</span>
                    </>
                ) : (
                    <>
                        <Mic size={iconSize} strokeWidth={2.5} />
                        <span>Quick Inbox</span>
                    </>
                )}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={start}
            disabled={processing}
            className={`${sizeCls} rounded-full flex items-center justify-center transition-all relative ${
                recording
                    ? "bg-red-500 text-white animate-pulse shadow-[0_0_28px_rgba(239,68,68,0.75)]"
                    : processing
                        ? "bg-meye-fuchsia/25 text-meye-fuchsia"
                        : "bg-gradient-to-br from-meye-fuchsia to-fuchsia-700 text-white shadow-[0_0_20px_rgba(217,70,239,0.55)] hover:scale-105"
            } ${className}`}
            data-testid={testid}
            aria-label={recording ? "Stop quick capture" : processing ? "Processing capture" : "Quick capture — tap to speak"}
            title={recording ? "Tap to stop" : "Quick Inbox — tap to speak"}
        >
            {processing ? (
                <Loader2 size={iconSize} className="animate-spin" />
            ) : recording ? (
                // Solid square = STOP affordance (universally understood)
                <span className="w-4 h-4 rounded-sm bg-white" />
            ) : (
                <span className="relative flex items-center justify-center">
                    <Mic size={iconSize} strokeWidth={2.5} />
                    <Zap size={iconSize / 2.4} strokeWidth={3} className="absolute -top-0.5 -right-1.5 text-meye-cyan drop-shadow" />
                </span>
            )}
        </button>
    );
};

export default QuickCaptureButton;
